/**
 * FeedbackDashboard Component
 *
 * Dashboard for viewing and managing all query feedback.
 * Shows statistics, list of feedback, and allows filtering.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  AlertCircle, Turtle, XCircle, Lightbulb, MessageSquare,
  Clock, CheckCircle, XOctagon, Filter, RefreshCw, ChevronDown,
  Eye, Loader
} from 'lucide-react';
import { useFeedback } from '../hooks/useFeedback';
import { useSettings } from '../hooks/useSettings';
import FeedbackDetailModal from './FeedbackDetailModal';

const Container = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 28px;
    height: 28px;
    color: #1A8B8B;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;

  .stat-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.$color || '#1A8B8B'}20;
    color: ${props => props.$color || '#1A8B8B'};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .stat-label {
    font-size: 13px;
    color: #718096;
    font-weight: 500;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #2d3748;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.div`
  position: relative;

  select {
    appearance: none;
    padding: 10px 36px 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #4a5568;
    background: white;
    cursor: pointer;
    min-width: 140px;

    &:focus {
      outline: none;
      border-color: #1A8B8B;
    }
  }

  svg {
    position: absolute;
    top: 50%;
    ${props => props.$isRTL ? 'left: 12px;' : 'right: 12px;'}
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: #a0aec0;
    pointer-events: none;
  }
`;

const RefreshButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-inline-start: auto;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FeedbackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeedbackCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1A8B8B;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
`;

const IssueTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$type) {
      case 'too_slow': return '#feebc8';
      case 'wrong_answer': return '#fed7d7';
      case 'could_be_better': return '#c6f6d5';
      case 'other': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'too_slow': return '#c05621';
      case 'wrong_answer': return '#c53030';
      case 'could_be_better': return '#276749';
      case 'other': return '#4a5568';
      default: return '#4a5568';
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'open': return '#bee3f8';
      case 'in_progress': return '#fefcbf';
      case 'resolved': return '#c6f6d5';
      case 'wont_fix': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'open': return '#2b6cb0';
      case 'in_progress': return '#975a16';
      case 'resolved': return '#276749';
      case 'wont_fix': return '#4a5568';
      default: return '#4a5568';
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CardContent = styled.div`
  margin-bottom: 12px;
`;

const QuestionPreview = styled.p`
  font-size: 14px;
  color: #2d3748;
  margin: 0 0 8px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CommentPreview = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-style: italic;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #a0aec0;
`;

const ReporterInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ViewButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(26, 139, 139, 0.1);
  color: #1A8B8B;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(26, 139, 139, 0.2);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #4a5568;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #1A8B8B;

  svg {
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #1A8B8B;
    color: #1A8B8B;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ISSUE_TYPE_ICONS = {
  too_slow: Turtle,
  wrong_answer: XCircle,
  could_be_better: Lightbulb,
  other: MessageSquare
};

const STATUS_ICONS = {
  open: Clock,
  in_progress: RefreshCw,
  resolved: CheckCircle,
  wont_fix: XOctagon
};

function FeedbackDashboard({ isActive = false }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Use ref for tracking initial load to avoid stale closure issues
  const hasLoadedRef = useRef(false);
  const prevActiveRef = useRef(isActive);

  const {
    feedbackList,
    feedbackStats,
    pagination,
    loading,
    getFeedbackList,
    getFeedbackStats
  } = useFeedback();

  const { language, isRTL } = useSettings();

  const loadData = useCallback(async (offset = 0) => {
    const filters = {
      limit: 20,
      offset
    };
    if (statusFilter) filters.status = statusFilter;
    if (typeFilter) filters.issueType = typeFilter;

    await Promise.all([
      getFeedbackList(filters),
      getFeedbackStats()
    ]);
  }, [statusFilter, typeFilter, getFeedbackList, getFeedbackStats]);

  // Load data when tab becomes active (detect transition from inactive to active)
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isActive;

    // Load if: becoming active and haven't loaded, OR already active on mount
    if (isActive && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadData();
    }
  }, [isActive, loadData]);

  // Re-fetch when filters change (only if already loaded once)
  useEffect(() => {
    if (hasLoadedRef.current) {
      loadData();
    }
  }, [statusFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewDetail = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedFeedbackId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      too_slow: { ar: 'بطيء جداً', en: 'Too Slow' },
      wrong_answer: { ar: 'إجابة خاطئة', en: 'Wrong Answer' },
      could_be_better: { ar: 'يمكن تحسينها', en: 'Could Be Better' },
      other: { ar: 'أخرى', en: 'Other' }
    };
    return labels[type]?.[language] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: { ar: 'مفتوح', en: 'Open' },
      in_progress: { ar: 'قيد المعالجة', en: 'In Progress' },
      resolved: { ar: 'تم الحل', en: 'Resolved' },
      wont_fix: { ar: 'لن يتم الإصلاح', en: "Won't Fix" }
    };
    return labels[status]?.[language] || status;
  };

  return (
    <Container $isRTL={isRTL}>
      <Header>
        <Title>
          <AlertCircle />
          {language === 'ar' ? 'مشاكل الاستعلامات' : 'Query Issues'}
        </Title>
        <Subtitle>
          {language === 'ar'
            ? 'تتبع ومعالجة المشاكل المُبلغ عنها في الاستعلامات'
            : 'Track and resolve reported query issues'}
        </Subtitle>
      </Header>

      {/* Stats Cards */}
      {feedbackStats && (
        <StatsGrid>
          <StatCard $color="#4299e1">
            <div className="stat-header">
              <div className="stat-icon"><Clock /></div>
              <span className="stat-label">
                {language === 'ar' ? 'مفتوحة' : 'Open'}
              </span>
            </div>
            <div className="stat-value">{feedbackStats.by_status?.open || 0}</div>
          </StatCard>

          <StatCard $color="#ecc94b">
            <div className="stat-header">
              <div className="stat-icon"><RefreshCw /></div>
              <span className="stat-label">
                {language === 'ar' ? 'قيد المعالجة' : 'In Progress'}
              </span>
            </div>
            <div className="stat-value">{feedbackStats.by_status?.in_progress || 0}</div>
          </StatCard>

          <StatCard $color="#48bb78">
            <div className="stat-header">
              <div className="stat-icon"><CheckCircle /></div>
              <span className="stat-label">
                {language === 'ar' ? 'تم الحل' : 'Resolved'}
              </span>
            </div>
            <div className="stat-value">{feedbackStats.by_status?.resolved || 0}</div>
          </StatCard>

          <StatCard $color="#1A8B8B">
            <div className="stat-header">
              <div className="stat-icon"><AlertCircle /></div>
              <span className="stat-label">
                {language === 'ar' ? 'آخر 7 أيام' : 'Last 7 Days'}
              </span>
            </div>
            <div className="stat-value">{feedbackStats.recent_7_days || 0}</div>
          </StatCard>
        </StatsGrid>
      )}

      {/* Filters */}
      <FilterBar>
        <Filter size={18} color="#718096" />

        <FilterSelect $isRTL={isRTL}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{language === 'ar' ? 'جميع الحالات' : 'All Status'}</option>
            <option value="open">{language === 'ar' ? 'مفتوح' : 'Open'}</option>
            <option value="in_progress">{language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</option>
            <option value="resolved">{language === 'ar' ? 'تم الحل' : 'Resolved'}</option>
            <option value="wont_fix">{language === 'ar' ? 'لن يتم الإصلاح' : "Won't Fix"}</option>
          </select>
          <ChevronDown />
        </FilterSelect>

        <FilterSelect $isRTL={isRTL}>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">{language === 'ar' ? 'جميع الأنواع' : 'All Types'}</option>
            <option value="too_slow">{language === 'ar' ? 'بطيء جداً' : 'Too Slow'}</option>
            <option value="wrong_answer">{language === 'ar' ? 'إجابة خاطئة' : 'Wrong Answer'}</option>
            <option value="could_be_better">{language === 'ar' ? 'يمكن تحسينها' : 'Could Be Better'}</option>
            <option value="other">{language === 'ar' ? 'أخرى' : 'Other'}</option>
          </select>
          <ChevronDown />
        </FilterSelect>

        <RefreshButton
          onClick={() => loadData()}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <RefreshCw className={loading ? 'animate-spin' : ''} />
          {language === 'ar' ? 'تحديث' : 'Refresh'}
        </RefreshButton>
      </FilterBar>

      {/* Feedback List */}
      {loading && feedbackList.length === 0 ? (
        <LoadingState>
          <Loader />
        </LoadingState>
      ) : feedbackList.length === 0 ? (
        <EmptyState>
          <AlertCircle />
          <h3>{language === 'ar' ? 'لا توجد بلاغات' : 'No Reports'}</h3>
          <p>
            {language === 'ar'
              ? 'لا توجد مشاكل مُبلغ عنها تطابق الفلاتر المحددة'
              : 'No reported issues match the selected filters'}
          </p>
        </EmptyState>
      ) : (
        <FeedbackList>
          {feedbackList.map((feedback, index) => {
            const IssueIcon = ISSUE_TYPE_ICONS[feedback.issue_type] || MessageSquare;
            const StatusIcon = STATUS_ICONS[feedback.status] || Clock;

            return (
              <FeedbackCard
                key={feedback.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleViewDetail(feedback.id)}
              >
                <CardHeader>
                  <IssueTypeBadge $type={feedback.issue_type}>
                    <IssueIcon />
                    {getIssueTypeLabel(feedback.issue_type)}
                  </IssueTypeBadge>
                  <StatusBadge $status={feedback.status}>
                    <StatusIcon />
                    {getStatusLabel(feedback.status)}
                  </StatusBadge>
                </CardHeader>

                <CardContent>
                  {feedback.message_content && (
                    <QuestionPreview>
                      {feedback.message_content}
                    </QuestionPreview>
                  )}
                  <CommentPreview>"{feedback.comment}"</CommentPreview>
                </CardContent>

                <CardFooter>
                  <ReporterInfo>
                    {feedback.reporter_display_name || feedback.reporter_name}
                  </ReporterInfo>
                  <DateInfo>
                    {formatDate(feedback.created_at)}
                  </DateInfo>
                  <ViewButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetail(feedback.id);
                    }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Eye />
                    {language === 'ar' ? 'عرض' : 'View'}
                  </ViewButton>
                </CardFooter>
              </FeedbackCard>
            );
          })}
        </FeedbackList>
      )}

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <Pagination>
          <PageButton
            disabled={pagination.offset === 0}
            onClick={() => loadData(Math.max(0, pagination.offset - pagination.limit))}
          >
            {language === 'ar' ? 'السابق' : 'Previous'}
          </PageButton>
          <span>
            {language === 'ar'
              ? `${pagination.offset + 1} - ${Math.min(pagination.offset + pagination.limit, pagination.total)} من ${pagination.total}`
              : `${pagination.offset + 1} - ${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total}`}
          </span>
          <PageButton
            disabled={!pagination.hasMore}
            onClick={() => loadData(pagination.offset + pagination.limit)}
          >
            {language === 'ar' ? 'التالي' : 'Next'}
          </PageButton>
        </Pagination>
      )}

      {/* Detail Modal */}
      <FeedbackDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
        feedbackId={selectedFeedbackId}
        onUpdate={() => loadData(pagination.offset)}
      />
    </Container>
  );
}

export default FeedbackDashboard;
